/**
 * SIL-1347 - subscribe to a CSS media query from React.
 *
 * Why a hook rather than Tailwind responsive utilities: the marketplace's
 * sub-760px reflow (spec section 4.1) is an ACCEPTANCE CRITERION with a
 * component test behind it, and a jsdom test cannot evaluate a media query
 * embedded in a class name - it can only assert that a particular class string
 * is present, which tests the implementation rather than the behaviour. Reading
 * the breakpoint through `matchMedia` makes the layout a value the component
 * branches on, so the test drives it the way a real viewport would.
 *
 * `matchMedia` is missing in a plain jsdom environment and in any non-browser
 * render, so absence reads as "does not match" rather than throwing.
 */
import { useEffect, useState } from 'react';

function evaluate(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => evaluate(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    // Re-read on mount: the initial state was computed during the first render,
    // which in a resize-during-mount race can already be stale.
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
